/* eslint-disable no-param-reassign */
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as BandedIntervalTrendLineLayerSpec } from '../../../../layer/preset/line/bandedIntervalTrendLine';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';
import type { Data } from '../../specProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface BandedIntervalTrendLineLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type BandedIntervalTrendLineLayerType = StandardChartLayerSpec &
  BandedIntervalTrendLineLayerSpec &
  BandedIntervalTrendLineLayerTypeWithFields;

// 创建带状区间趋势折线图数据类型验证器
const validateBandedIntervalTrendLineDataTypes = createDataTypeValidator(
  'bandedIntervalTrendLine',
  [
    { field: 'x', type: 'date' },
    { field: 'y', type: 'number' },
    { field: 'y2', type: 'number' },
    { field: 'y3', type: 'number' }
  ]
);

// 创建带状区间趋势折线图编码验证器
const validateBandedIntervalTrendLineEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'y2' },
  { field: 'y3' }
]);

// 额外的检查函数
const additionalBandedIntervalTrendLineChecks = (
  layer: BandedIntervalTrendLineLayerType,
  data: Data
) => {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  // 检查数据量是否足够
  if (values.length <= 2) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires at least 3 data points to show a meaningful trend`
    );
    layer.__isEncodingValid = false;
    return;
  }

  // 检查区间上下限合理性
  let hasInvalidInterval = false;
  values.forEach(item => {
    const y2Value = parseFloat(String(item[encoding.y2]));
    const y3Value = parseFloat(String(item[encoding.y3]));
    if (y2Value < y3Value) {
      hasInvalidInterval = true;
    }
  });

  if (hasInvalidInterval) {
    utils.logger.warn(
      'warning',
      `The upper bound (y2) should be greater than or equal to the lower bound (y3) in "${layer.type}" layer`
    );
    layer.__isEncodingValid = false;
  }
};

// 创建并导出带状区间趋势折线图处理器
export const bandedIntervalTrendLine = createLayerProcessor<BandedIntervalTrendLineLayerType>(
  'bandedIntervalTrendLine',
  validateBandedIntervalTrendLineEncoding,
  validateBandedIntervalTrendLineDataTypes,
  additionalBandedIntervalTrendLineChecks
);
